import React, { useState, useEffect, useMemo } from 'react';
import Pagination from '../../components/Common/Pagination';
import { TableLoader } from '../../components/Common/Loader';
import { ProductForm } from './ProductForm';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchProducts,
  fetchProductById,
  clearSelectedProduct,
  setSelectedCategory,
  setSearchQuery,
  setStatusTab,
  setCurrentPage,
  setLimit,
} from '../../store/productsSlice';
import type { ProductItem } from '../../api/products';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';
import { ViewProductModal } from './components/ViewProductModal';

export interface ProductsListProps {
  onNavigate?: (path: string, data?: any) => void;
}

const renderTextLabel = (val: any, fallback: string = '-'): string => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    return String(
      val.name ||
      val.brand_name ||
      val.brandName ||
      val.category_name ||
      val.title ||
      val.sizeValue ||
      val.variantName ||
      val.username ||
      fallback
    );
  }
  return fallback;
};

export const ProductsList: React.FC<ProductsListProps> = () => {
  const dispatch = useAppDispatch();

  const {
    products,
    categories,
    summary,
    pagination,
    loading,
    error,
    selectedCategory,
    searchQuery,
    statusTab,
    currentPage,
    limit,
    selectedProduct,
    loadingProduct,
  } = useAppSelector((state) => state.products);

  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [editingProductItem, setEditingProductItem] = useState<any>(null);
  const [viewingProductItem, setViewingProductItem] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);

  // Fetch products on mount and when filters change
  useEffect(() => {
    dispatch(
      fetchProducts({
        category_id: selectedCategory,
        page: currentPage,
        limit: limit,
        search: searchQuery,
        status: statusTab,
      })
    );
  }, [dispatch, selectedCategory, currentPage, limit, statusTab, searchQuery]);

  // Handle local search debounce or immediate search trigger
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    dispatch(setSearchQuery(query));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      dispatch(
        fetchProducts({
          category_id: selectedCategory,
          page: 1,
          limit: limit,
          search: searchQuery,
          status: statusTab,
        })
      );
    }
  };

  // Filter products locally if needed (or display returned server products)
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((product) => {
      // Status tab filter
      if (statusTab === 'Active' && product.is_active !== 1) return false;
      if (statusTab === 'Inactive' && product.is_active !== 0) return false;

      // Category filter
      if (
        selectedCategory &&
        selectedCategory !== 'cat_all' &&
        selectedCategory !== 'all'
      ) {
        const matchesCategoryId = (String(product.category_id).toLowerCase() === String(selectedCategory).toLowerCase());
        const matchesCategoryName =
          product.category_name?.toLowerCase() === selectedCategory.toLowerCase();
        if (!matchesCategoryId && !matchesCategoryName) {
          return false;
        }
      }

      // Search query filter (client side filter fallback)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = renderTextLabel(product.name).toLowerCase().includes(query);
        const matchesSku = renderTextLabel(product.sku_code).toLowerCase().includes(query);
        const matchesHsn = renderTextLabel((product as any).hsn_code || (product as any).tax_rate?.hsnCode).toLowerCase().includes(query);
        const matchesBrand = renderTextLabel(product.brand).toLowerCase().includes(query);
        const matchesVariant = renderTextLabel(product.variant).toLowerCase().includes(query);
        const matchesSize = renderTextLabel(product.size).toLowerCase().includes(query);
        return matchesName || matchesSku || matchesHsn || matchesBrand || matchesVariant || matchesSize;
      }

      return true;
    });
  }, [products, statusTab, selectedCategory, searchQuery]);

  // Derived Counts
  const activeCount = summary ? summary.active_count : products.filter((p) => p.is_active === 1).length;
  const inactiveCount = summary ? summary.inactive_count : products.filter((p) => p.is_active === 0).length;

  const handleProductSubmit = (_formData: any) => {
    // Re-fetch list after adding/updating product
    dispatch(fetchProducts());
    dispatch(clearSelectedProduct());
    setEditingProductItem(null);
    setView('list');
  };

  const handleViewProduct = (product: ProductItem) => {
    setViewingProductItem(product);
    setIsViewModalOpen(true);
    dispatch(fetchProductById(product.id));
  };

  const handleEditProduct = (id: number | string) => {
    const found = products.find((p) => String(p.id) === String(id));
    setEditingProductItem(found || null);
    setView('edit');
    dispatch(fetchProductById(id));
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Product Name', 'SKU Code', 'HSN Code', 'GST (%)', 'Crate Name', 'Units/Crate', 'Category', 'Brand', 'Variant', 'Size', 'Status'];
    const rows = filteredProducts.map((p: any) => [
      renderTextLabel(p.id),
      renderTextLabel(p.name),
      renderTextLabel(p.sku_code, ''),
      renderTextLabel(p.hsn_code, ''),
      p.gst_percent || p.gst || 0,
      renderTextLabel(p.crate?.name || p.crate_name || p.crateName, ''),
      p.units_per_case || '',
      renderTextLabel(p.category_name || p.category, ''),
      renderTextLabel(p.brand, ''),
      renderTextLabel(p.variant, ''),
      renderTextLabel(p.size, ''),
      p.is_active === 1 ? 'Active' : 'Inactive',
    ]);
    exportToCSV(`products_${statusTab.toLowerCase()}`, headers, rows);
  };

  if (view === 'add') {
    return (
      <ProductForm
        onSubmit={handleProductSubmit}
        onCancel={() => setView('list')}
      />
    );
  }

  if (view === 'edit') {
    return (
      <ProductForm
        isEditMode={true}
        initialData={selectedProduct || editingProductItem}
        isLoading={loadingProduct && !editingProductItem}
        onSubmit={handleProductSubmit}
        onCancel={() => {
          dispatch(clearSelectedProduct());
          setEditingProductItem(null);
          setView('list');
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Control Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-7 h-7 rounded-full go_erp-bg transition-all flex items-center justify-center text-white cursor-pointer"
            title="Go Back"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h3 className="text-xl font-bold go_erp-text tracking-wide m-0">Products</h3>
        </div>

        {/* Middle Status Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = statusTab === tab;
            return (
              <button
                key={tab}
                onClick={() => dispatch(setStatusTab(tab))}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${isActive
                  ? 'go_erp-bg text-white shadow-sm'
                  : 'text-slate-650 hover:text-slate-900'
                  }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Right buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView('add')}
            className="go_erp-bg text-white text-sm px-4 py-1.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Product
          </button>

          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Counts Row */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>
          Active Counts: <span className="font-extrabold">{activeCount}</span>
        </span>
        <span>
          Inactive Counts: <span className="font-extrabold">{inactiveCount}</span>
        </span>
      </div>

      {/* Category Selection Grid/Rows */}
      {categories && categories.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 p-2.5 rounded-xl">
          {categories.map((category) => {
            const isSelected =
              selectedCategory === category.id ||
              selectedCategory === category.slug ||
              ((selectedCategory === 'cat_all' || selectedCategory === 'all') &&
                (category.id === 'cat_all' || category.slug === 'all'));
            return (
              <button
                key={category.id}
                onClick={() => dispatch(setSelectedCategory(String(category.id)))}
                className={`py-2 px-3 text-xs font-semibold rounded-full transition-all capitalize cursor-pointer text-center select-none ${isSelected ? 'go_erp-bg text-white' : 'bg-white text-slate-650 hover:bg-slate-50'
                  }`}
              >
                {category.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={limit}
              onChange={(e) => dispatch(setLimit(Number(e.target.value)))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Table Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search (Press Enter)"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-48 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {error && !loading && (
          <div className="py-6 px-6 text-center text-xs font-semibold text-red-500 bg-red-50">
            Error: {error}
          </div>
        )}

        {/* Scrollable Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse min-w-[1400px]">
            <thead>
              <tr className="bg-[#FFF8E1]">
                {[
                  { label: 'Id' },
                  { label: 'Name' },
                  { label: 'SAP Code' },
                  { label: 'HSN Code' },
                  { label: 'GST (%)' },
                  { label: 'Crate Name' },
                  { label: 'Units/Crate' },
                  { label: 'Active' },
                  { label: 'BRAND' },
                  { label: 'SIZE' },
                  { label: 'Created By' },
                  { label: 'Actions', alignRight: true },
                ].map((col) => (
                  <th
                    key={col.label}
                    className={`px-4 py-4 text-xs text-[#1F1F1F] font-semibold ${(col as any).alignRight ? 'text-right' : ''
                      }`}
                  >
                    <div className={`flex items-center gap-1 ${(col as any).alignRight ? 'justify-end' : ''}`}>
                      <span>{col.label}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {loading ? (
                <TableLoader colSpan={12} text="Loading..." />
              ) : filteredProducts.length > 0 ? (
                  filteredProducts.map((product: ProductItem) => (
                    <tr key={product.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-4 text-xs text-[#828282]">{renderTextLabel(product.id)}</td>
                      <td className="px-4 py-4 text-xs text-[#828282] font-semibold" title={renderTextLabel(product.name)}>
                        {renderTextLabel(product.name)}
                      </td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{renderTextLabel(product.sku_code)}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{renderTextLabel((product as any).hsn_code || (product as any).tax_rate?.hsnCode)}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{typeof product.gst_percent === 'number' ? product.gst_percent.toFixed(2) : renderTextLabel((product as any).gst, '0.00')}</td>
                      <td className="px-4 py-4 text-xs text-[#828282] font-semibold">{renderTextLabel(product.crate?.name || product.crate_name || (product as any).crateName)}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{renderTextLabel(product.units_per_case)}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{product.is_active === 1 ? 'Active' : 'Inactive'}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{renderTextLabel(product.brand)}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{renderTextLabel(product.variant)}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{renderTextLabel(product.created_by)}</td>
                      <td className="px-4 py-4 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleViewProduct(product)}
                            className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                            title="View Product Details"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button
                            onClick={() => handleEditProduct(product.id)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                            title="Edit Product"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={12} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                      No products found matching filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        {/* Table Footer / Pagination */}
        <Pagination
          currentPage={pagination ? pagination.page : currentPage}
          totalEntries={pagination ? pagination.total_records : filteredProducts.length}
          entriesLimit={pagination ? pagination.limit : limit}
          onPageChange={(page) => dispatch(setCurrentPage(page))}
        />
      </div>
      {/* View Product Modal */}
      <ViewProductModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingProductItem(null);
        }}
        product={selectedProduct || viewingProductItem}
        isLoading={loadingProduct && !selectedProduct}
        onEdit={() => {
          setIsViewModalOpen(false);
          if (viewingProductItem?.id) {
            handleEditProduct(viewingProductItem.id);
          }
        }}
      />
    </div>
  );
};

export default ProductsList;
